import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../../../ui/sheet";
import { Button } from "../../../ui/button";
import { Badge } from "../../../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import {
  Sparkles,
  Brain,
  TrendingUp,
  Lightbulb,
  Target,
  ChevronRight,
  Loader2,
  RefreshCw,
  BookOpen,
  Share2,
  Download,
  Calendar,
  Plus,
  CheckSquare,
} from "lucide-react";
import { AISynthesisResult } from "./types";
import { UserTakeaway, createTakeaway } from "../../../../api/userTakeaways";
import { cn } from "../../../../lib/utils";
import useRetryFetch from "../../../../hooks/useRetryFetch";
import { useProjectIndex } from "../../../../hooks/useProjectIndex";
import { synthesizeInsights } from "../../../../api/insightAI";
import { ProjectPill, projectColor } from "../ProjectChip";
import { useSpaces } from "../../../../context/SpaceProvider";
import { toast } from "sonner";
import { GeneratingLoader } from "../../../ui/generating-loader";

interface AISynthesisPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  insights: UserTakeaway[];
  onViewThemeInsights: (themeName: string, insightIds: string[]) => void;
  onViewCategory: (categoryId: string) => void;
}

type SynthesisPeriod = "week" | "month" | "quarter" | "all";

const AISynthesisPanel: React.FC<AISynthesisPanelProps> = ({
  open,
  onOpenChange,
  insights,
  onViewThemeInsights,
  onViewCategory,
}) => {
  const { t } = useTranslation();
  const retryFetch = useRetryFetch();
  const { projectById } = useProjectIndex();
  const { spaces } = useSpaces();
  const [period, setPeriod] = useState<SynthesisPeriod>("month");
  const [isLoading, setIsLoading] = useState(false);
  const [synthesis, setSynthesis] = useState<AISynthesisResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [addingActionIndex, setAddingActionIndex] = useState<number | null>(null);

  // Filter insights by period
  const getFilteredInsights = (period: SynthesisPeriod): UserTakeaway[] => {
    const now = new Date();
    let cutoffDate: Date;

    switch (period) {
      case "week":
        cutoffDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case "month":
        cutoffDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case "quarter":
        cutoffDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        break;
      case "all":
      default:
        return insights;
    }

    return insights.filter((i) => new Date(i.createdOn) >= cutoffDate);
  };

  // Generate synthesis when period changes or panel opens
  useEffect(() => {
    if (open && insights.length > 0) {
      generateSynthesis();
    }
  }, [open, period]);

  const generateSynthesis = async () => {
    setIsLoading(true);
    setError(null);

    const filteredInsights = getFilteredInsights(period);

    if (filteredInsights.length === 0) {
      setSynthesis(null);
      setIsLoading(false);
      return;
    }

    try {
      // Try API first
      const response = await synthesizeInsights(retryFetch, {
        insightIds: filteredInsights.map((i) => i._id),
        period,
      });

      if (response.status === "SUCCESS" && response.synthesis) {
        setSynthesis(response.synthesis);
      } else {
        // Fallback to local synthesis
        const localSynthesis = generateLocalSynthesis(filteredInsights);
        setSynthesis(localSynthesis);
      }
    } catch (err) {
      console.error("AI synthesis failed, using local fallback:", err);
      // Generate local synthesis
      const localSynthesis = generateLocalSynthesis(filteredInsights);
      setSynthesis(localSynthesis);
    } finally {
      setIsLoading(false);
    }
  };

  // Local synthesis fallback
  const generateLocalSynthesis = (filteredInsights: UserTakeaway[]): AISynthesisResult => {
    // Group by category
    const categoryGroups = new Map<string, UserTakeaway[]>();
    const uncategorized: UserTakeaway[] = [];

    filteredInsights.forEach((insight) => {
      if (insight.category) {
        const key = insight.category.name;
        if (!categoryGroups.has(key)) {
          categoryGroups.set(key, []);
        }
        categoryGroups.get(key)!.push(insight);
      } else {
        uncategorized.push(insight);
      }
    });

    // Create themes from categories
    const themes = Array.from(categoryGroups.entries())
      .map(([name, items]) => ({
        name,
        insightCount: items.length,
        summary: generateThemeSummary(name, items),
        insightIds: items.map((i) => i._id),
      }))
      .sort((a, b) => b.insightCount - a.insightCount)
      .slice(0, 5);

    // Add uncategorized as "General Reflections" if significant
    if (uncategorized.length > 2) {
      themes.push({
        name: t("general_reflections") || "General Reflections",
        insightCount: uncategorized.length,
        summary: t("various_insights_captured") || "Various insights captured during this period",
        insightIds: uncategorized.slice(0, 5).map((i) => i._id),
      });
    }

    // Generate recommendations based on patterns
    const recommendations = generateRecommendations(filteredInsights, themes);

    // Identify growth areas
    const growthAreas = identifyGrowthAreas(filteredInsights);

    const periodLabel = {
      week: t("this_week") || "This Week",
      month: t("this_month") || "This Month",
      quarter: t("this_quarter") || "This Quarter",
      all: t("all_time") || "All Time",
    }[period];

    return {
      period: periodLabel,
      totalInsights: filteredInsights.length,
      themes,
      recommendations,
      growthAreas,
    };
  };

  const generateThemeSummary = (category: string, items: UserTakeaway[]): string => {
    const count = items.length;
    const categoryLower = category.toLowerCase();

    // Generate contextual summaries
    if (categoryLower.includes("leadership")) {
      return count > 3
        ? t("leadership_focus_summary") || "You're actively developing your leadership philosophy"
        : t("leadership_emerging_summary") || "Leadership themes are emerging in your reflections";
    }
    if (categoryLower.includes("communication")) {
      return t("communication_summary") || "Communication skills appear frequently in your insights";
    }
    if (categoryLower.includes("growth") || categoryLower.includes("learning")) {
      return t("growth_summary") || "You're focused on continuous learning and development";
    }
    if (categoryLower.includes("strategy") || categoryLower.includes("planning")) {
      return t("strategy_summary") || "Strategic thinking is a recurring theme";
    }

    return `${count} ${t("insights_about") || "insights about"} ${category.toLowerCase()}`;
  };

  const generateRecommendations = (
    insights: UserTakeaway[],
    themes: AISynthesisResult["themes"]
  ): string[] => {
    const recommendations: string[] = [];

    // Check if there's a dominant theme
    if (themes.length > 0 && themes[0].insightCount > insights.length * 0.3) {
      recommendations.push(
        `${t("consider_deepening") || "Consider deepening your"} ${themes[0].name.toLowerCase()} ${t("knowledge_with_engagement") || "knowledge with a focused engagement"}`
      );
    }

    // Check for action-oriented insights
    const actionInsights = insights.filter(
      (i) =>
        i.content.toLowerCase().includes("need to") ||
        i.content.toLowerCase().includes("should") ||
        i.content.toLowerCase().includes("will ")
    );
    if (actionInsights.length > 2) {
      recommendations.push(
        t("action_recommendation") || "You have several action-oriented insights. Consider converting them to action items."
      );
    }

    // Check for uncategorized insights
    const uncategorized = insights.filter((i) => !i.category);
    if (uncategorized.length > insights.length * 0.4) {
      recommendations.push(
        t("categorize_recommendation") || "Many insights are uncategorized. Adding categories helps identify patterns."
      );
    }

    // General recommendations if none specific
    if (recommendations.length === 0) {
      recommendations.push(
        t("reflection_recommendation") || "Review your top insights and consider how they connect to your goals."
      );
    }

    return recommendations.slice(0, 3);
  };

  const identifyGrowthAreas = (insights: UserTakeaway[]): string[] => {
    const areas: string[] = [];

    // Look for keywords indicating growth areas
    const contentLower = insights.map((i) => i.content.toLowerCase()).join(" ");

    if (contentLower.includes("listen") || contentLower.includes("communication")) {
      areas.push(t("active_listening") || "Active Listening");
    }
    if (contentLower.includes("delegate") || contentLower.includes("trust")) {
      areas.push(t("delegation_trust") || "Delegation & Trust");
    }
    if (contentLower.includes("feedback") || contentLower.includes("difficult conversation")) {
      areas.push(t("giving_feedback") || "Giving Feedback");
    }
    if (contentLower.includes("time") || contentLower.includes("priorit")) {
      areas.push(t("time_management") || "Time Management");
    }
    if (contentLower.includes("decision") || contentLower.includes("choice")) {
      areas.push(t("decision_making") || "Decision Making");
    }

    return areas.slice(0, 4);
  };

  const getPeriodLabel = (p: SynthesisPeriod) => {
    switch (p) {
      case "week":
        return t("last_7_days") || "Last 7 days";
      case "month":
        return t("last_30_days") || "Last 30 days";
      case "quarter":
        return t("last_90_days") || "Last 90 days";
      case "all":
        return t("all_time") || "All time";
    }
  };

  const handleAddAsActionItem = async (recommendation: string, index: number) => {
    setAddingActionIndex(index);
    try {
      const response = await createTakeaway(retryFetch, {
        type: "action",
        content: recommendation,
        source: {
          type: "user_created",
        },
      });

      if (response.status === "CREATED") {
        toast.success(t("action_item_created") || "Action item created successfully");
      } else {
        toast.error(t("failed_to_create_action_item") || "Failed to create action item");
      }
    } catch (error) {
      console.error("Failed to create action item from recommendation:", error);
      toast.error(t("failed_to_create_action_item") || "Failed to create action item");
    } finally {
      setAddingActionIndex(null);
    }
  };

  const filteredInsightCount = getFilteredInsights(period).length;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-[500px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-100 to-purple-100 flex items-center justify-center">
              <Brain className="h-4 w-4 text-violet-600" />
            </div>
            {t("ai_insights_synthesis") || "AI Insights Synthesis"}
          </SheetTitle>
          <SheetDescription>
            {t("synthesis_description") ||
              "AI analysis of your insights to identify themes, patterns, and growth opportunities."}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Period Selector */}
          <div className="flex items-center justify-between">
            <Select value={period} onValueChange={(v) => setPeriod(v as SynthesisPeriod)}>
              <SelectTrigger className="w-[160px]">
                <Calendar className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="week">{getPeriodLabel("week")}</SelectItem>
                <SelectItem value="month">{getPeriodLabel("month")}</SelectItem>
                <SelectItem value="quarter">{getPeriodLabel("quarter")}</SelectItem>
                <SelectItem value="all">{getPeriodLabel("all")}</SelectItem>
              </SelectContent>
            </Select>

            <Button
              variant="ghost"
              size="sm"
              onClick={generateSynthesis}
              disabled={isLoading}
            >
              <RefreshCw className={cn("h-4 w-4", isLoading && "animate-spin")} />
            </Button>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-16">
              <GeneratingLoader context="synthesis" />
            </div>
          )}

          {/* No Insights State */}
          {!isLoading && filteredInsightCount === 0 && (
            <div className="text-center py-8">
              <Lightbulb className="h-12 w-12 text-gray-200 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">
                {t("no_insights_for_period") ||
                  "No insights found for this period. Try selecting a longer time range."}
              </p>
            </div>
          )}

          {/* Synthesis Results */}
          {!isLoading && synthesis && (
            <div className="space-y-6">
              {/* Summary Stats */}
              <div className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-100">
                <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center shadow-sm">
                  <Brain className="h-6 w-6 text-violet-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-violet-900">
                    {synthesis.totalInsights}
                  </p>
                  <p className="text-sm text-violet-600">
                    {t("insights_analyzed") || "insights analyzed"} • {synthesis.period}
                  </p>
                </div>
              </div>

              {/* Themes */}
              {synthesis.themes.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-violet-500" />
                    {t("key_themes") || "Key Themes"}
                  </h3>
                  <div className="space-y-2">
                    {synthesis.themes.map((theme, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          if (theme.insightIds.length > 0) {
                            onViewThemeInsights(theme.name, theme.insightIds);
                          }
                        }}
                        className="w-full text-left p-3 rounded-lg border border-gray-100 hover:border-violet-200 hover:bg-violet-50/30 transition-all group"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900">
                                {theme.name}
                              </span>
                              <Badge variant="secondary" className="text-xs">
                                {theme.insightCount}
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-5">
                              {theme.summary}
                            </p>
                            {((theme.projectIds?.length ?? 0) > 0 || (theme.spaceIds?.length ?? 0) > 0) && (
                              <div className="flex flex-wrap items-center gap-1 mt-1.5">
                                {(theme.projectIds ?? []).map((id) => {
                                  const project = projectById[id];
                                  if (!project) return null;
                                  return (
                                    <ProjectPill
                                      key={id}
                                      project={project}
                                      color={projectColor(project, spaces)}
                                      variant="badge"
                                    />
                                  );
                                })}
                                {(theme.spaceIds ?? []).map((id) => {
                                  const space = (spaces ?? []).find((s) => s._id === id);
                                  if (!space) return null;
                                  return (
                                    <span
                                      key={id}
                                      className="inline-flex items-center h-5 rounded-full border text-[10px] px-1.5"
                                      style={{ color: space.color, borderColor: `${space.color}50`, background: `${space.color}0d` }}
                                    >
                                      {space.name}
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                          <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-violet-500 transition-colors flex-shrink-0 mt-1" />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Recommendations */}
              {synthesis.recommendations.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-amber-500" />
                    {t("ai_recommendations") || "AI Recommendations"}
                  </h3>
                  <div className="space-y-2">
                    {synthesis.recommendations.map((rec, index) => (
                      <div
                        key={index}
                        className="p-3 rounded-lg bg-amber-50 border border-amber-100 group hover:border-amber-200 transition-colors"
                      >
                        <p className="text-sm text-amber-900 mb-2">{rec}</p>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs text-primary hover:text-primary hover:bg-primary/5"
                          onClick={() => handleAddAsActionItem(rec, index)}
                          disabled={addingActionIndex === index}
                        >
                          {addingActionIndex === index ? (
                            <>
                              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                              {t("adding") || "Adding..."}
                            </>
                          ) : (
                            <>
                              <CheckSquare className="h-3 w-3 mr-1" />
                              {t("add_as_action_item") || "Add as Action Item"}
                            </>
                          )}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Growth Areas */}
              {synthesis.growthAreas.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <Target className="h-4 w-4 text-green-500" />
                    {t("growth_areas") || "Growth Areas Identified"}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {synthesis.growthAreas.map((area, index) => (
                      <Badge
                        key={index}
                        variant="outline"
                        className="text-xs bg-green-50 text-green-700 border-green-200"
                      >
                        {area}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="pt-4 border-t space-y-2">
                <Button variant="outline" className="w-full justify-start" disabled>
                  <Share2 className="h-4 w-4 mr-2" />
                  {t("share_with_mentor") || "Share with Mentor"} ({t("coming_soon") || "Soon"})
                </Button>
                <Button variant="outline" className="w-full justify-start" disabled>
                  <Download className="h-4 w-4 mr-2" />
                  {t("export_report") || "Export Report"} ({t("coming_soon") || "Soon"})
                </Button>
                <Button variant="outline" className="w-full justify-start" disabled>
                  <BookOpen className="h-4 w-4 mr-2" />
                  {t("suggested_engagements") || "View Suggested Engagements"} ({t("coming_soon") || "Soon"})
                </Button>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default AISynthesisPanel;
